// src/components/Packaging/PackagingItemModal.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import toast from "react-hot-toast";
import { compressImage } from "../../utils/imageCompression";
import { usePackaging } from "../../contexts/PackagingContext";
import { useStorage } from "../../contexts/StorageContext";
import CameraCapture from "../Common/CameraCapture";

const defaultCategories = ["Box", "Protective", "Label", "Insert", "Filler"];

const inputGlass =
  "w-full px-3 py-2 rounded bg-black/30 border border-white/20 text-white placeholder:text-white/60 outline-none";
const pill =
  "px-4 py-2 rounded text-sm bg-zinc-700 hover:bg-zinc-600 text-white";
const primary =
  "px-4 py-2 rounded text-sm bg-orange-500 hover:bg-orange-600 text-white";
const secondary = "px-4 py-2 rounded text-sm bg-gray-600 text-white";
const tabBtn = (active) =>
  `px-4 py-2 rounded ${active ? "bg-orange-500 text-white" : "bg-zinc-700 text-white hover:bg-zinc-600"}`;

const MAX_IMAGE_BYTES = 2 * 1024 * 1024;

const SUPPLIERS = [
  { name: "Pack Queen", href: "https://www.packqueen.com.au/" },
  { name: "Heaps Good Packaging", href: "https://heapsgoodpackaging.com.au/" },
  { name: "Sydney Packaging", href: "https://www.sydneypackaging.com.au/" },
];

function toNum(v, d = 0) {
  const n = Number(v);
  return Number.isFinite(n) ? n : d;
}

function uniqueSku(base, existingSkus) {
  const used = new Set((existingSkus || []).filter(Boolean));
  let candidate = base;
  let i = 1;
  while (used.has(candidate)) {
    candidate = `${base}-${i}`;
    i = i + 1;
  }
  return candidate;
}

function detectLocation() {
  try {
    const raw = JSON.parse(localStorage.getItem("mudoUserSettings") || "{}");
    const parts = [raw.location, raw.city, raw.region, raw.state, raw.country, raw.countryName]
      .filter(Boolean)
      .map((s) => String(s).trim());
    const fromSettings = parts.find((p) => p.length > 1);
    if (fromSettings) return fromSettings;
  } catch {}
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || "";
    const city = tz.split("/")[1]?.replace(/_/g, " ");
    if (city) return city;
  } catch {}
  try {
    const lang = navigator.language || "";
    const m = lang.match(/-([A-Z]{2})$/);
    const cc = m?.[1];
    if (cc) {
      const map = { AU: "Australia", NZ: "New Zealand", US: "United States", CA: "Canada", GB: "United Kingdom", IE: "Ireland" };
      return map[cc] || cc;
    }
  } catch {}
  return "";
}

const buildGoogleUrl = (q) => `https://www.google.com/search?q=${encodeURIComponent(q || "")}`;
const normalizeUrl = (val) => {
  const v = String(val || "").trim();
  if (!v) return "";
  if (/^https?:\/\//i.test(v)) return v;
  return `https://${v}`;
};

export default function PackagingItemModal({ open, onClose, initialItem, categoryOptions }) {
  const { items = [], addItem, updateItem } = usePackaging();
  const { loadJSON, saveJSON } = useStorage();

  const editMode = !!(initialItem && initialItem.id != null);

  const [tab, setTab] = useState("basic");
  const [cats, setCats] = useState(defaultCategories);
  const [errors, setErrors] = useState({});
  const [imgError, setImgError] = useState("");

  const dropRef = useRef(null);
  const fileInputRef = useRef(null);
  const firstFieldRef = useRef(null);

  const [cameraOpen, setCameraOpen] = useState(false);

  // Load categories from storage
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const stored = await loadJSON("packaging.categories.json", defaultCategories);
        if (alive && Array.isArray(stored) && stored.length) setCats(stored);
      } catch {
        // keep defaults
      }
    })();
    return () => { alive = false; };
  }, [loadJSON]);

  const [form, setForm] = useState(
    editMode
      ? {
          id: initialItem?.id ?? null,
          name: initialItem?.name || "",
          sku: initialItem?.sku || "",
          category: initialItem?.category || "Box",
          supplier: initialItem?.supplier || initialItem?.supplierName || "",
          link: initialItem?.link || initialItem?.supplierLink || "",
          image: initialItem?.image || null,
          uom: initialItem?.uom || "each",
          pricingMode: initialItem?.pricingMode || "unit",
          unitPrice: initialItem?.unitPrice !== undefined ? String(initialItem.unitPrice) : "",
          packPrice: initialItem?.packPrice !== undefined ? String(initialItem.packPrice) : "",
          unitsPerPack: initialItem?.unitsPerPack !== undefined ? String(initialItem.unitsPerPack) : "",
          yieldPerUnit: String(initialItem?.yieldPerUnit ?? 1),
          wastePct: String(initialItem?.wastePct ?? 0),
          taxFlag: initialItem?.taxFlag || "inclusive",
          dims_l: initialItem?.dims?.l ?? "",
          dims_w: initialItem?.dims?.w ?? "",
          dims_h: initialItem?.dims?.h ?? "",
          dims_unit: initialItem?.dims?.unit || "mm",
          weight_value: initialItem?.weight?.value ?? "",
          weight_unit: initialItem?.weight?.unit || "g",
          lowStockLimit: initialItem?.lowStockLimit !== undefined && initialItem?.lowStockLimit !== null ? String(initialItem.lowStockLimit) : "",
          stock: initialItem?.stock !== undefined && initialItem?.stock !== null ? String(initialItem.stock) : (initialItem?.quantity !== undefined && initialItem?.quantity !== null ? String(initialItem.quantity) : ""),
          notes: initialItem?.notes || initialItem?.note || "",
        }
      : {
          id: null,
          name: "",
          sku: "",
          category: "Box",
          supplier: "",
          link: "",
          image: null,
          uom: "each",
          pricingMode: "unit",
          unitPrice: "",
          packPrice: "",
          unitsPerPack: "",
          yieldPerUnit: "1",
          wastePct: "0",
          taxFlag: "inclusive",
          dims_l: "",
          dims_w: "",
          dims_h: "",
          dims_unit: "mm",
          weight_value: "",
          weight_unit: "g",
          lowStockLimit: "",
          stock: "",
          notes: "",
        }
  );

  // Auto-generate SKU based on category, name, and dimensions
  useEffect(() => {
    if (editMode || !open) return;
    
    const cat = (form.category || "PKG").slice(0, 3).toUpperCase();
    const namePrefix = (form.name || "NEW").slice(0, 4).toUpperCase().replace(/\s/g, '');
    
    // Include dimensions in SKU if available
    let sizeStr = "";
    if (form.dims_l && form.dims_w && form.dims_h) {
      sizeStr = `-${Math.round(form.dims_l)}x${Math.round(form.dims_w)}x${Math.round(form.dims_h)}`;
    }
    
    const ts = Date.now().toString().slice(-4);
    const base = `${cat}-${namePrefix}${sizeStr}-${ts}`;
    
    const existingSkus = (items || []).map((m) => m.sku);
    setForm((f) => (f.sku ? f : { ...f, sku: uniqueSku(base, existingSkus) }));
  }, [form.name, form.category, form.dims_l, form.dims_w, form.dims_h, editMode, items, open]);

  const change = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
  };
  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // Image handling
  async function acceptImageFile(file) {
    if (!file) return;
    setImgError("");
    const allowed = ["image/png", "image/jpeg", "image/webp", "image/svg+xml", "image/heic", "image/heif"];
    if (!allowed.includes(file.type)) {
      setImgError("Please upload PNG, JPG/JPEG, WEBP, or SVG.");
      return;
    }
    try {
      const { dataURL } = await compressImage(file, {
        maxBytes: MAX_IMAGE_BYTES,
        maxWidth: 2000,
        maxHeight: 2000,
        preferredMime: "image/webp",
      });
      setField("image", dataURL);
    } catch (e) {
      console.error("Image compression failed:", e);
      setImgError("Could not process that image.");
    }
  }

  const onFileChange = async (e) => {
    const f = e.target.files?.[0];
    if (f) await acceptImageFile(f);
    e.target.value = "";
  };
  const pickFile = () => fileInputRef.current?.click();

  const openCamera = () => setCameraOpen(true);
  const closeCamera = () => setCameraOpen(false);
  const onCameraCapture = async (blob) => {
    try {
      const file = new File([blob], "capture.jpg", { type: "image/jpeg" });
      await acceptImageFile(file);
      toast.success("Photo captured");
    } catch (e) {
      toast.error("Couldn't use that photo");
    } finally {
      closeCamera();
    }
  };

  const removeImage = () => setField("image", null);

  // Drag and drop + paste
  useEffect(() => {
    const el = dropRef.current;
    if (!el) return;
    const stop = (e) => { e.preventDefault(); e.stopPropagation(); };
    const onDrop = async (e) => {
      stop(e);
      const f = e.dataTransfer?.files?.[0];
      if (f) await acceptImageFile(f);
      el.classList.remove("ring-2", "ring-amber-400");
    };
    const onDragOver = (e) => { stop(e); el.classList.add("ring-2", "ring-amber-400"); };
    const onDragLeave = (e) => { stop(e); el.classList.remove("ring-2", "ring-amber-400"); };
    const onPaste = async (e) => {
      const items = e.clipboardData?.items || [];
      for (const it of items) {
        if (it.type?.startsWith("image/")) {
          const f = it.getAsFile();
          if (f) { await acceptImageFile(f); break; }
        }
      }
    };
    el.addEventListener("dragover", onDragOver);
    el.addEventListener("dragleave", onDragLeave);
    el.addEventListener("drop", onDrop);
    el.addEventListener("dragenter", stop);
    el.addEventListener("dragend", stop);
    el.addEventListener("paste", onPaste);
    return () => {
      el.removeEventListener("dragover", onDragOver);
      el.removeEventListener("dragleave", onDragLeave);
      el.removeEventListener("drop", onDrop);
      el.removeEventListener("dragenter", stop);
      el.removeEventListener("dragend", stop);
      el.removeEventListener("paste", onPaste);
    };
  }, []);

  // Calculate unit price
  const unitBase = useMemo(() => {
    if (form.pricingMode === "unit") return toNum(form.unitPrice, 0);
    const pp = toNum(form.packPrice, 0);
    const up = Math.max(1, toNum(form.unitsPerPack, 1));
    return up > 0 ? pp / up : 0;
  }, [form.pricingMode, form.unitPrice, form.packPrice, form.unitsPerPack]);

  const unitWithWaste = useMemo(() => {
    const wm = 1 + Math.max(0, toNum(form.wastePct, 0)) / 100;
    return unitBase * wm;
  }, [unitBase, form.wastePct]);

  const perUnitEffective = useMemo(() => {
    switch (form.taxFlag) {
      case "exclusive":
        return unitWithWaste * 1.1;
      case "none":
        return unitWithWaste;
      case "inclusive":
      default:
        return unitWithWaste;
    }
  }, [unitWithWaste, form.taxFlag]);

  const validate = () => {
    const e = {};
    if (!form.name?.trim()) e.name = "Name is required";
    if (!form.sku?.trim()) e.sku = "SKU failed to generate";
    if (form.pricingMode === "unit" && !(toNum(form.unitPrice, NaN) >= 0)) {
      e.unitPrice = "Enter a valid unit price";
    }
    if (form.pricingMode === "pack") {
      if (!(toNum(form.packPrice, NaN) >= 0)) e.packPrice = "Enter a valid pack price";
      if (!(toNum(form.unitsPerPack, NaN) >= 1)) e.unitsPerPack = "Units per pack must be ≥ 1";
    }
    
    // Check SKU uniqueness
    const clashing = (items || []).some(
      (it) =>
        String(it.sku || "").trim().toLowerCase() === form.sku.toLowerCase() &&
        (!editMode || String(it.id) !== String(initialItem.id))
    );
    if (clashing) e.sku = "SKU already exists";

    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const doSave = (after) => {
    if (!validate()) {
      toast.error("Please fix the highlighted fields.");
      return;
    }
    try {
      const payload = {
        id: form.id ?? initialItem?.id ?? null,
        name: (form.name || "").trim() || "Untitled",
        sku: (form.sku || "").trim(),
        category: form.category,
        supplier: (form.supplier || "").trim(),
        link: normalizeUrl(form.link),
        image: form.image || null,
        uom: form.uom,
        pricingMode: form.pricingMode,
        unitPrice: form.pricingMode === "unit" ? toNum(form.unitPrice, 0) : null,
        packPrice: form.pricingMode === "pack" ? toNum(form.packPrice, 0) : null,
        unitsPerPack: form.pricingMode === "pack" ? Math.max(1, toNum(form.unitsPerPack, 1)) : null,
        yieldPerUnit: Math.max(0, toNum(form.yieldPerUnit, 1)),
        wastePct: Math.max(0, toNum(form.wastePct, 0)),
        taxFlag: form.taxFlag,
        dims: {
          l: form.dims_l === "" ? null : toNum(form.dims_l),
          w: form.dims_w === "" ? null : toNum(form.dims_w),
          h: form.dims_h === "" ? null : toNum(form.dims_h),
          unit: form.dims_unit || "mm",
        },
        weight: {
          value: form.weight_value === "" ? null : toNum(form.weight_value),
          unit: form.weight_unit || "g",
        },
        lowStockLimit: form.lowStockLimit === "" ? null : toNum(form.lowStockLimit, 0),
        stock: form.stock === "" ? null : toNum(form.stock, 0),
        notes: (form.notes || "").trim(),
        updatedAt: Date.now(),
      };

      if (editMode) {
        updateItem(initialItem.id, payload);
      } else {
        addItem(payload);
      }
      toast.success(editMode ? "Updated" : "Saved");
      after?.();
    } catch (err) {
      console.error(err);
      toast.error("Could not save packaging item.");
    }
  };

  const save = () => doSave(onClose);
  const saveAdd = () =>
    doSave(() =>
      setForm({
        id: null,
        name: "",
        sku: "",
        category: "Box",
        supplier: "",
        link: "",
        image: null,
        uom: "each",
        pricingMode: "unit",
        unitPrice: "",
        packPrice: "",
        unitsPerPack: "",
        yieldPerUnit: "1",
        wastePct: "0",
        taxFlag: "inclusive",
        dims_l: "",
        dims_w: "",
        dims_h: "",
        dims_unit: "mm",
        weight_value: "",
        weight_unit: "g",
        lowStockLimit: "",
        stock: "",
        notes: "",
      })
    );

  // Category management
  const addCategory = async () => {
    const n = prompt("New category");
    if (!n) return;
    const next = Array.from(new Set([...(cats || []), n.trim()]));
    setCats(next);
    try { await saveJSON("packaging.categories.json", next); } catch {}
    setForm((f) => ({ ...f, category: n.trim() }));
  };

  const removeCategory = async () => {
    if (!form.category) return;
    if (!confirm(`Remove "${form.category}" from list?`)) return;
    const next = (cats || []).filter((c) => c !== form.category);
    setCats(next);
    try { await saveJSON("packaging.categories.json", next); } catch {}
    setForm((f) => ({ ...f, category: "" }));
  };

  // Focus on open
  useEffect(() => {
    if (!open) return;
    setTimeout(() => firstFieldRef.current?.focus?.(), 50);
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "s") {
        e.preventDefault();
        save();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]); // eslint-disable-line

  if (!open) return null;

  const locationHint = useMemo(() => detectLocation(), []);
  const queryBits = ["packaging supplies", locationHint, form.name, form.category].filter(Boolean).join(" ");
  const portalTarget = document.getElementById("modal-root") || document.body;

  return createPortal(
    <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <div className="relative w-full max-w-6xl mx-4">
        <div className="bg-white/10 dark:bg-black/40 text-white rounded-xl border border-white/20 shadow-2xl overflow-hidden">
          {/* Header */}
          <div className="px-6 py-4 border-b border-white/15 flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold">{editMode ? "Edit Packaging Item" : "Add Packaging Item"}</h3>
              <p className="text-xs opacity-70">Set costs in the package's unit — we'll compute per-unit price.</p>
            </div>
            <button onClick={onClose} className={pill}>Close</button>
          </div>

          {/* Tabs */}
          <div className="px-6 pt-4 flex gap-3">
            <button className={tabBtn(tab === "basic")} onClick={() => setTab("basic")}>Basic Info</button>
            <button className={tabBtn(tab === "stock")} onClick={() => setTab("stock")}>Stock & Cost</button>
          </div>

          {/* Body */}
          <div className="p-6 grid grid-cols-1 md:grid-cols-4 gap-6">
            {/* Left: Image */}
            <div className="md:col-span-1">
              <label className="block text-sm mb-2 opacity-80">Image</label>
              <div
                ref={dropRef}
                className="aspect-square rounded-lg border border-white/20 bg-black/30 flex items-center justify-center overflow-hidden cursor-pointer"
                onClick={() => fileInputRef.current?.click()}
              >
                {form.image ? (
                  <img src={typeof form.image === "string" ? form.image : ""} alt={form.name || "Packaging"} className="h-full w-full object-cover" />
                ) : (
                  <div className="text-xs opacity-70 text-center px-3">Drag & drop a photo here<br />or click / paste</div>
                )}
              </div>
              {imgError && <p className="text-xs text-red-300 mt-2">{imgError}</p>}
              <div className="flex gap-2 mt-3">
                <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={onFileChange} />
                <button onClick={pickFile} className="px-3 py-1 rounded bg-amber-400 text-black text-sm hover:bg-amber-300">Upload</button>
                <button onClick={openCamera} className="px-3 py-1 rounded bg-emerald-400 text-black text-sm hover:bg-emerald-300">Camera</button>
                {form.image && <button onClick={removeImage} className="ml-auto px-3 py-1 rounded bg-red-600 text-white text-sm hover:bg-red-500">Remove</button>}
              </div>
              <p className="mt-2 text-[11px] text-white/60">Images are automatically resized under 2 MB.</p>

              {/* Per-unit estimate */}
              <div className="mt-4 rounded-lg border border-white/10 bg-black/30 p-3">
                <div className="text-sm font-semibold mb-2">Per-unit estimate</div>
                <div className="text-2xl font-bold">${perUnitEffective.toFixed(4)}</div>
                <div className="text-xs text-white/70 mt-1">
                  Base: ${unitBase.toFixed(4)} • Waste: {Math.max(0, toNum(form.wastePct, 0))}%
                </div>
                <div className="text-xs text-white/70">
                  Tax: {form.taxFlag} • Yield: {Math.max(0, toNum(form.yieldPerUnit, 1))}
                </div>
              </div>
            </div>

            {/* Middle: Fields */}
            {tab === "basic" ? (
              <div className="md:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="sm:col-span-2">
                  <label className="block text-sm mb-1">Name *</label>
                  <input
                    ref={firstFieldRef}
                    name="name"
                    value={form.name}
                    onChange={change}
                    className={inputGlass}
                    placeholder="e.g., 200 box"
                  />
                  {errors.name && <p className="text-xs text-red-300 mt-1">{errors.name}</p>}
                </div>
                <div>
                  <label className="block text-sm mb-1">Category</label>
                  <div className="flex items-center gap-2">
                    <select name="category" value={form.category} onChange={change} className={`${inputGlass} grow`}>
                      {cats.map((c) => <option key={c} value={c}>{c}</option>)}
                    </select>
                    <button type="button" onClick={addCategory} className="px-3 rounded bg-yellow-400 text-black text-sm hover:bg-yellow-300">Add</button>
                    <button type="button" onClick={removeCategory} disabled={!form.category} className="px-3 rounded bg-zinc-700 text-white text-sm disabled:opacity-40">Remove</button>
                  </div>
                </div>
                <div>
                  <label className="block text-sm mb-1">Unit of Measure</label>
                  <select name="uom" value={form.uom} onChange={change} className={inputGlass}>
                    <option value="each">each</option>
                    <option value="meter">meter</option>
                    <option value="sheet">sheet</option>
                    <option value="label">label</option>
                    <option value="unit">unit</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm mb-1">SKU (auto-generated)</label>
                  <input name="sku" value={form.sku} readOnly className={`${inputGlass} cursor-not-allowed opacity-70`} />
                  {errors.sku && <p className="text-xs text-red-300 mt-1">{errors.sku}</p>}
                </div>
                <div>
                  <label className="block text-sm mb-1">Supplier</label>
                  <input name="supplier" value={form.supplier} onChange={change} className={inputGlass} placeholder="Supplier name" />
                </div>
                <div className="sm:col-span-2">
                  <label className="block text-sm mb-1">Product Link / Website</label>
                  <input type="url" name="link" value={form.link} onChange={change} placeholder="https://" className={inputGlass} />
                </div>

                {/* Dimensions */}
                <div className="sm:col-span-2">
                  <label className="block text-sm mb-2">Dimensions (L × W × H)</label>
                  <div className="grid grid-cols-4 gap-2">
                    <input name="dims_l" value={form.dims_l} onChange={change} placeholder="L" className={inputGlass} />
                    <input name="dims_w" value={form.dims_w} onChange={change} placeholder="W" className={inputGlass} />
                    <input name="dims_h" value={form.dims_h} onChange={change} placeholder="H" className={inputGlass} />
                    <select name="dims_unit" value={form.dims_unit} onChange={change} className={inputGlass}>
                      <option value="mm">mm</option>
                      <option value="cm">cm</option>
                      <option value="in">in</option>
                    </select>
                  </div>
                </div>

                {/* Weight */}
                <div>
                  <label className="block text-sm mb-1">Weight</label>
                  <input name="weight_value" type="number" step="0.1" value={form.weight_value} onChange={change} className={inputGlass} placeholder="Value" />
                </div>
                <div>
                  <label className="block text-sm mb-1">Weight Unit</label>
                  <select name="weight_unit" value={form.weight_unit} onChange={change} className={inputGlass}>
                    <option value="g">g</option>
                    <option value="kg">kg</option>
                    <option value="oz">oz</option>
                    <option value="lb">lb</option>
                  </select>
                </div>
              </div>
            ) : (
              <div className="md:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm mb-1">Pricing Mode</label>
                  <select name="pricingMode" value={form.pricingMode} onChange={change} className={inputGlass}>
                    <option value="unit">Per unit</option>
                    <option value="pack">Per pack</option>
                  </select>
                </div>
                {form.pricingMode === "unit" ? (
                  <div>
                    <label className="block text-sm mb-1">Unit Price</label>
                    <input
                      name="unitPrice"
                      type="number"
                      min="0"
                      step="0.0001"
                      value={form.unitPrice}
                      onChange={change}
                      className={inputGlass}
                      placeholder="2.25"
                    />
                    {errors.unitPrice && <p className="text-xs text-red-300 mt-1">{errors.unitPrice}</p>}
                  </div>
                ) : (
                  <>
                    <div>
                      <label className="block text-sm mb-1">Pack Price</label>
                      <input
                        name="packPrice"
                        type="number"
                        min="0"
                        step="0.01"
                        value={form.packPrice}
                        onChange={change}
                        className={inputGlass}
                        placeholder="28.00"
                      />
                      {errors.packPrice && <p className="text-xs text-red-300 mt-1">{errors.packPrice}</p>}
                    </div>
                    <div>
                      <label className="block text-sm mb-1">Units per Pack</label>
                      <input
                        name="unitsPerPack"
                        type="number"
                        min="1"
                        step="1"
                        value={form.unitsPerPack}
                        onChange={change}
                        className={inputGlass}
                        placeholder="50"
                      />
                      {errors.unitsPerPack && <p className="text-xs text-red-300 mt-1">{errors.unitsPerPack}</p>}
                    </div>
                  </>
                )}
                <div>
                  <label className="block text-sm mb-1">Yield / Unit</label>
                  <input name="yieldPerUnit" type="number" min="0" step="0.0001" value={form.yieldPerUnit} onChange={change} className={inputGlass} />
                </div>
                <div>
                  <label className="block text-sm mb-1">Waste %</label>
                  <input name="wastePct" type="number" min="0" step="0.1" value={form.wastePct} onChange={change} className={inputGlass} placeholder="0" />
                </div>
                <div>
                  <label className="block text-sm mb-1">Tax</label>
                  <select name="taxFlag" value={form.taxFlag} onChange={change} className={inputGlass}>
                    <option value="inclusive">GST/VAT inclusive</option>
                    <option value="exclusive">GST/VAT exclusive</option>
                    <option value="none">No tax</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm mb-1">Low Stock Alert</label>
                  <input
                    name="lowStockLimit"
                    type="number"
                    min="0"
                    value={form.lowStockLimit}
                    onChange={change}
                    className={inputGlass}
                    placeholder="Alert me at this qty"
                  />
                </div>
                <div>
                  <label className="block text-sm mb-1">Current Stock</label>
                  <input
                    name="stock"
                    type="number"
                    min="0"
                    value={form.stock}
                    onChange={change}
                    className={inputGlass}
                    placeholder="Current quantity"
                  />
                </div>
                <div className="sm:col-span-2">
                  <label className="block text-sm mb-1">Notes</label>
                  <textarea
                    name="notes"
                    rows={3}
                    value={form.notes}
                    onChange={change}
                    className={inputGlass}
                    placeholder="Optional: batch #, special handling notes"
                  />
                </div>
              </div>
            )}

            {/* Right: Suppliers */}
            <aside className="md:col-span-1 space-y-3 bg-white/5 rounded-lg border border-white/10 p-4">
              <h4 className="font-semibold">Suppliers & Web</h4>
              <button
                type="button"
                className="w-full px-3 py-2 rounded bg-blue-600 text-white text-sm hover:bg-blue-700"
                onClick={() => window.open(buildGoogleUrl(queryBits), "_blank", "noopener,noreferrer")}
              >
                Search: "packaging {locationHint}"
              </button>
              {SUPPLIERS.map((s) => (
                <a
                  key={s.name}
                  href={s.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block w-full px-3 py-2 text-center rounded bg-emerald-600 text-white text-sm hover:bg-emerald-700"
                >
                  {s.name}
                </a>
              ))}
              <p className="text-xs text-white/70">
                Search uses your location ({locationHint || "unknown"}) plus item details.
              </p>
              {form.link && (
                <a href={form.link} target="_blank" rel="noopener noreferrer" className="inline-block text-xs underline text-amber-300">
                  Visit product link ↗
                </a>
              )}
            </aside>
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t border-white/15 flex items-center justify-end gap-2">
            <button onClick={onClose} className={secondary}>Cancel</button>
            {editMode ? (
              <button onClick={save} className={primary}>Save Changes</button>
            ) : (
              <>
                <button onClick={save} className={primary}>Save</button>
                <button onClick={saveAdd} className={pill}>Save & Add</button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Camera overlay */}
      {cameraOpen && (
        <div className="fixed inset-0 z-[10050] flex items-center justify-center">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={closeCamera} />
          <div className="relative w-full max-w-3xl mx-4 rounded-2xl overflow-hidden bg-white dark:bg-neutral-900 text-neutral-900 dark:text-white border border-white/10 shadow-2xl">
            <div className="flex items-center justify-between px-3 py-2 border-b border-black/10 dark:border-white/10">
              <div className="text-sm font-medium">Capture photo</div>
              <button className="px-2 py-1 text-xs rounded border border-black/10 dark:border-white/20" onClick={closeCamera}>
                Close
              </button>
            </div>
            <div className="p-3">
              <CameraCapture
                preferBackCamera
                onCapture={onCameraCapture}
                onClose={closeCamera}
              />
              <div className="text-xs opacity-70 px-1 pt-2">Tip: If the video is paused on iOS, tap "Start / Unblock".</div>
            </div>
          </div>
        </div>
      )}
    </div>,
    portalTarget
  );
}
